import { useEffect, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import useMaster from "hook/useMaster";
import useStaticMaster from "hook/useStaticMaster";
import { flattenObject, showToast } from "utils/utility";
import { IAffiliateGroupList, TChargeUserType } from "types";
import { useAffiliateGroupData, useFlightMarkupsData } from "libs/data-access";

import { getRole } from "utils/routeUtility";
import { IFlightMarkupB2C, IFlightMarkupB2CDetails, IFlightMarkupList } from "./types";

export default function useFlightMarkupForm() {

    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    // Get 'mno' query parameter
    const mno = searchParams.get('mno');
    const {
        service,
        userChargeType
    } = useParams<{
        service: "flight" | "hotel";
        userChargeType: TChargeUserType;
    }>();


    const {
        getMarkupFormCharges,
        markupFormChargesDropdown,
        isLoadingMarkupFormCharges,
        getSearchType,
        searchTypeDropdown,
        isLoadingSearchType,
        getTravelType,
        travelTypeDropdown,
        isLoadingTravelType,
        getFlightChargeApplyFor,
        flightChargeApplyForDropdown,
        isLoadingFlightChargeApplyFor,
        getFlightApplyType,
        flightApplyTypeDropdown,
        isLoadingFlightApplyType,
    } = useStaticMaster();
    const {
        groupTypes,
        getAllGroupTypes,

        flightAllClassTypes,
        getFlightAllClassTypes,
        flightCabinClassTypes,
        getFlightCabinClassTypes,
        supplierList,
        getSupplierList,
        airlinesList,
        getAirlinesBySuppliers,
        allCountryList,
        getAllCountryList,
        airportByCoutries,
        getAllAirportByCountries,
        airportByDestinationCoutries,
        getAllAirportByDestinationCountries
    } = useMaster();
    const {
        getFlightMarkupsData,
        postFlightMarkupsData,
        putFlightMarkupsData
    } = useFlightMarkupsData();


    const [markup, setMarkup] = useState<IFlightMarkupB2C>({
        id: 0,
        isLoading: false,
        details: {
            affiliateGroupIds: [],
            flightAirlineIds: [],
            flightTicketSupplierIds: [],
            flightOriginCountryIds: [],
            flightDestinationCountryIds: [],
            originAirportIds: [],
            destinationAirportIds: [],
            flightMarkupApplyType: [],
            markupChargeWithAmountTypes: [],
            flightChargeApplyFor: null,
            flightTypes: [],
            flightSearchTypes: [],
            startDate: null,
            endDate: null,
            minimumGrossPrice: null,
            maximumGrossPrice: null,
            flightClass: [],
            flightCabin: [],
            gst: getRole().includes("AFFILIATE") ? false : true,
            userType: "ADMIN_CUSTOMER"
        },
        rules: {
            affiliateGroupIds: "NO",
            flightAirlineIds: "NO",
            flightTicketSupplierIds: "NO",
            flightOriginCountryIds: "NO",
            flightDestinationCountryIds: "NO",
            originAirportIds: "NO",
            destinationAirportIds: "NO",
            flightMarkupApplyType: "YES",
            markupChargeWithAmountTypes: "YES",
            flightChargeApplyFor: "YES",
            flightTypes: "YES",
            flightSearchTypes: "YES",
            startDate: "NO",
            endDate: "NO",
            minimumGrossPrice: "NO",
            maximumGrossPrice: "NO",
            flightClass: "NO",
            flightCabin: "NO",
            gst: "NO",
            userType: "NO"
        },
        errors: {
            affiliateGroupIds: undefined,
            flightAirlineIds: undefined,
            flightTicketSupplierIds: undefined,
            flightOriginCountryIds: undefined,
            flightDestinationCountryIds: undefined,
            originAirportIds: undefined,
            destinationAirportIds: undefined,
            flightMarkupApplyType: undefined,
            markupChargeWithAmountTypes: undefined,
            flightChargeApplyFor: undefined,
            flightTypes: undefined,
            flightSearchTypes: undefined,
            startDate: undefined,
            endDate: undefined,
            minimumGrossPrice: undefined,
            maximumGrossPrice: undefined,
            flightClass: undefined,
            flightCabin: undefined,
            gst: undefined,
            userType: undefined
        }
    });

    useEffect(() => {
        if (service !== "flight" || !userChargeType) {
            navigate(-1);
            return;
        };

        getMarkupFormCharges();
        getFlightAllClassTypes();
        getFlightCabinClassTypes();
        getSupplierList("FLIGHT");
        getAllCountryList();

        getSearchType();
        getTravelType();

        getFlightChargeApplyFor();
        getFlightApplyType();
        getAllCountryList();

        setMarkup(prev => ({
            ...prev,
            details: {
                ...prev.details,
                userType: userChargeType
            }
        }));

        if (mno) {
            getFlightMarkupData();
        };
    }, []);

    useEffect(() => {
        if (markupFormChargesDropdown.length > 0) {
            setMarkup(prev => ({
                ...prev,
                details: {
                    ...prev.details,
                    markupChargeWithAmountTypes: markupFormChargesDropdown.map(each => ({
                        markupFormCharge: String(each.value),
                        amount: 0,
                        feeType: "FIXED"
                    }))
                }
            }));
        };
    }, [markupFormChargesDropdown]);


    // HANDLER FUNCTIONS
    const convertResponseToDetails = (data: IFlightMarkupList): IFlightMarkupB2CDetails => {
        // get all airport by countries
        getAllAirportByCountries(data.originCountryResponses?.map(country => String(country.id)) || []);
        getAllAirportByDestinationCountries(data.destinationCountryResponses?.map(country => String(country.id)) || []);
        getAirlinesBySuppliers(data.supplierResponses?.map(supplier => String(supplier.id)) || []);

        return {
            affiliateGroupIds: data.affiliateGroup?.map(group => group.id) || [],
            flightAirlineIds: data.flightAirlineResponses?.map(airline => airline.id) || [],
            flightTicketSupplierIds: data.supplierResponses?.map(supplier => supplier.id) || [],
            flightOriginCountryIds: data.originCountryResponses?.map(country => country.id) || [],
            flightDestinationCountryIds: data.destinationCountryResponses?.map(country => country.id) || [],
            originAirportIds: data.originAirportResponses?.map(airport => airport.id) || [],
            destinationAirportIds: data.destinationAirportResponses?.map(airport => airport.id) || [],
            flightMarkupApplyType: data.flightApplyTypes?.map(type => type.value) || [],
            markupChargeWithAmountTypes: data.markupChargeWithAmountTypes?.map(charge => ({
                markupFormCharge: charge.markupFormCharge.value,
                amount: charge.amount,
                feeType: charge.feeType
            })) || [],
            flightChargeApplyFor: data.flightChargeApplyFor?.value || null,
            flightTypes: data.flightTypes?.map(type => type.value) || [],
            flightSearchTypes: data.flightSearchTypes?.map(type => type.value) || [],
            startDate: data.startDate || null,
            endDate: data.endDate || null,
            minimumGrossPrice: data.minimumGrossPrice || null,
            maximumGrossPrice: data.maximumGrossPrice || null,
            flightClass: data.flightClass?.map(cls => cls.value) || [],
            flightCabin: data.flightCabin?.map(cabin => cabin.value) || [],
            gst: data.gst ?? false,
            userType: data.userType as TChargeUserType || "ADMIN_CUSTOMER"
        };
    };

    const handleChange = (field: keyof IFlightMarkupB2C["details"], value: any) => {
        setMarkup(prev => ({
            ...prev,
            details: {
                ...prev.details,
                [field]: value
            },
            errors: {
                ...prev.errors,
                [field]: undefined
            }
        }));
    };

    const handleChangeMarkupChargeWithAmountTypes = (
        index: number,
        field: keyof IFlightMarkupB2C["details"]["markupChargeWithAmountTypes"][number],
        value: any
    ) => {
        setMarkup(prev => ({
            ...prev,
            details: {
                ...prev.details,
                markupChargeWithAmountTypes: prev.details.markupChargeWithAmountTypes.map((each, i) => {
                    if (i === index) {
                        return { ...each, [field]: value };
                    };
                    return each;
                })
            }
        }));
    };


    const validate = () => {
        const errors: IFlightMarkupB2C["errors"] = {
            affiliateGroupIds: (
                markup.rules.affiliateGroupIds === "YES" && !markup.details.affiliateGroupIds.length
            ) ? "Affiliate group is mandatory" : undefined,
            flightAirlineIds: (
                markup.rules.flightAirlineIds === "YES" && !markup.details.flightAirlineIds.length
            ) ? "Flight airline is mandatory" : undefined,
            flightTicketSupplierIds: (
                markup.rules.flightTicketSupplierIds === "YES" && !markup.details.flightTicketSupplierIds.length
            ) ? "Flight ticket supplier is mandatory" : undefined,
            flightOriginCountryIds: (
                markup.rules.flightOriginCountryIds === "YES" && !markup.details.flightOriginCountryIds.length
            ) ? "Flight origin country is mandatory" : undefined,
            flightDestinationCountryIds: (
                markup.rules.flightDestinationCountryIds === "YES" && !markup.details.flightDestinationCountryIds.length
            ) ? "Flight destination country is mandatory" : undefined,
            originAirportIds: (
                markup.rules.originAirportIds === "YES" && !markup.details.originAirportIds.length
            ) ? "Origin airport is mandatory" : undefined,
            destinationAirportIds: (
                markup.rules.destinationAirportIds === "YES" && !markup.details.destinationAirportIds.length
            ) ? "Destination airport is mandatory" : undefined,
            markupChargeWithAmountTypes: (
                markup.rules.markupChargeWithAmountTypes === "YES" && !markup.details.markupChargeWithAmountTypes.length
            ) ? "Markup charge with amount type is mandatory" : undefined,
            flightChargeApplyFor: (
                markup.rules.flightChargeApplyFor === "YES" && !markup.details.flightChargeApplyFor
            ) ? "Flight charge apply for is mandatory" : undefined,
            flightMarkupApplyType: (
                markup.rules.flightMarkupApplyType === "YES" && !markup.details.flightMarkupApplyType.length
            ) ? "Flight markup apply type is mandatory" : undefined,
            flightTypes: (
                markup.rules.flightTypes === "YES" && !markup.details.flightTypes.length
            ) ? "Flight type is mandatory" : undefined,
            flightSearchTypes: (
                markup.rules.flightSearchTypes === "YES" && !markup.details.flightSearchTypes.length
            ) ? "Flight search type is mandatory" : undefined,
            startDate: (
                markup.rules.startDate === "YES" && !markup.details.startDate
            ) ? "Start date is mandatory" : undefined,
            endDate: (
                markup.rules.endDate === "YES" && !markup.details.endDate
            ) ? "End date is mandatory" : undefined,
            minimumGrossPrice: (
                markup.rules.minimumGrossPrice === "YES" && !markup.details.minimumGrossPrice
            ) ? "Minimum gross price is mandatory" : undefined,
            maximumGrossPrice: (
                markup.rules.maximumGrossPrice === "YES" && !markup.details.maximumGrossPrice
            ) ? "Maximum gross price is mandatory" : undefined,
            flightClass: (
                markup.rules.flightClass === "YES" && !markup.details.flightClass.length
            ) ? "Flight class is mandatory" : undefined,
            flightCabin: (
                markup.rules.flightCabin === "YES" && !markup.details.flightCabin.length
            ) ? "Flight cabin is mandatory" : undefined,
            gst: (
                markup.rules.gst === "YES" && !markup.details.gst
            ) ? "GST is mandatory" : undefined,
            userType: (
                markup.rules.userType === "YES" && !markup.details.userType
            ) ? "User type is mandatory" : undefined
        };

        const isAmountValid = markup.details.markupChargeWithAmountTypes.some(each => {
            if (!each.amount) {
                errors.markupChargeWithAmountTypes = "Commission charge is mandatory";
                return false;
            };
            return true;
        });

        setMarkup(prev => ({ ...prev, errors }));

        let isValid = Object.values(flattenObject(errors)).some(val => !val);

        if (!isAmountValid) {
            isValid = false;
            showToast({
                type: "error",
                message: "Commission charge is mandatory"
            });
        };

        // console.log("Is valid: ", isValid);
        // console.log("Errors: ", errors);

        return isValid;
    };

    const handleSaveMarkup = async () => {
        if (!validate()) return;

        if (markup.id) {
            await updateMarkup();
        } else {
            await saveMarkup();
        };
    };


    // API CALLS
    const getFlightMarkupData = async () => {
        const response = await getFlightMarkupsData(`/${mno}`);

        if (response.isSuccess) {
            // console.log("Response for get markup data: ", response.data?.data);
            const data: IFlightMarkupList = response.data?.data;

            const convertedDetails = convertResponseToDetails(data);
            console.log("convertedDetails: ", convertedDetails);
            setMarkup(prev => ({
                ...prev,
                id: data.id,
                details: convertedDetails
            }));
        } else {
            console.log("Error while getting hotel markup data: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while getting hotel markup data"
            });
        };
    };

    const saveMarkup = async () => {
        if (!validate()) return;

        setMarkup(prev => ({ ...prev, isLoading: true }));

        const response = await postFlightMarkupsData(``, markup.details);

        setMarkup(prev => ({ ...prev, isLoading: false }));

        if (response.isSuccess) {
            navigate(-1);
        } else {
            console.log("Error while saving markup: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while saving markup"
            });
        };
    };

    const updateMarkup = async () => {
        setMarkup(prev => ({ ...prev, isLoading: true }));

        const response = await putFlightMarkupsData(`/${markup.id}`, { ...markup.details, userType: null });

        setMarkup(prev => ({ ...prev, isLoading: false }));

        if (response.isSuccess) {
            navigate(-1);
        } else {
            console.log("Error while updating markup: ", response);
            showToast({
                type: "error",
                message: response.message || "Error while updating markup"
            });
        };
    };


    return {
        navigate,
        markupFormChargesDropdown,
        searchTypeDropdown,
        travelTypeDropdown,
        groupTypes,
        flightAllClassTypes,
        flightCabinClassTypes,
        supplierList,
        airlinesList,
        getAirlinesBySuppliers,
        allCountryList,
        airportByCoutries,
        getAllAirportByCountries,
        airportByDestinationCoutries,
        getAllAirportByDestinationCountries,
        flightChargeApplyForDropdown,
        flightApplyTypeDropdown,
        handleChange,
        handleChangeMarkupChargeWithAmountTypes,
        markup,
        handleSaveMarkup
    };

}